import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import SearchReportType from '@/Enums/ReportingDashboard/Search/ReportType.enum';
import ReportRequest from '@/Enums/ReportingDashboard/SearchReportRequest.enum';
import type {
  IBaseDataReport,
  ICampaignReport,
  IDateReport,
  ITotalReport,
} from '@/Interfaces/ReportingDashboard/Search/Report.interface';
import type {
  IAdGroupReportRecord,
  IAdvertiserReportRecord,
  IBaseReportRecord,
  IKeywordReportRecord,
  IPlatformReportRecord,
} from '@/Interfaces/ReportingDashboard/Search/ReportRecord.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Search/State.interface';
import { defaultTotals } from '@/constants/reportingDashboard/search';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { searchReportRequestHandler } from '@/helpers/reportingDashboard/search';
import type { RootState } from '@/store';
import type { SearchReportView } from '@/types/ReportingDashboard/Search.type';
import type { RequestMap } from '@/types/RequestMap.types';
import { AsyncThunk, AsyncThunkOptions, createAsyncThunk } from '@reduxjs/toolkit';

enum ActionPrefix {
  Totals = 'reportingDashboardSearch/getTotalsReport',

  DateReport = 'reportingDashboardSearch/getDateReport',
  PlatformReport = 'reportingDashboardSearch/getPlatformReport',
  AdvertiserReport = 'reportingDashboardSearch/getAdvertiserReport',
  CampaignReport = 'reportingDashboardSearch/getCampaignReport',
  AdGroupReport = 'reportingDashboardSearch/getAdGroupReport',
  KeywordReport = 'reportingDashboardSearch/getKeywordReport',
}

const shouldFetchExplicitReport = (report: IBaseReportRecord[], requestId: string | null) => {
  if (requestId === DEFAULT_LOADING_REQUEST_ID) {
    return true;
  }
  return !report?.length && !requestId;
};

const getExplicitReportThunkOptions = (
  dataField: keyof IState,
  requestIdField?: keyof IState,
): AsyncThunkOptions<IAbortSignalThunkArg> => ({
  condition: (_, { getState }) => {
    const requestIdKey = requestIdField ?? (`${dataField}RequestId` as keyof IState);
    const {
      reportingDashboardSearch: { [dataField]: data, [requestIdKey]: requestId },
    } = getState() as RootState;

    return shouldFetchExplicitReport(data as IBaseReportRecord[], requestId as string);
  },
});

export const getTotalReport = createAsyncThunk<ITotalReport, IAbortSignalThunkArg>(
  ActionPrefix.Totals,
  searchReportRequestHandler(SearchReportType.Total, { totals: defaultTotals }),
);

//#region grid
export const getDateReport = createAsyncThunk<IDateReport, IAbortSignalThunkArg>(
  ActionPrefix.DateReport,
  searchReportRequestHandler(SearchReportType.Date),
  getExplicitReportThunkOptions('dateReport'),
);

export const getPlatformReport = createAsyncThunk<IBaseDataReport<IPlatformReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.PlatformReport,
  searchReportRequestHandler(SearchReportType.Platform),
  getExplicitReportThunkOptions('platformReport'),
);

export const getAdvertiserReport = createAsyncThunk<IBaseDataReport<IAdvertiserReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdvertiserReport,
  searchReportRequestHandler(SearchReportType.Advertiser),
  getExplicitReportThunkOptions('advertiserReport'),
);

export const getCampaignReport = createAsyncThunk<ICampaignReport, IAbortSignalThunkArg>(
  ActionPrefix.CampaignReport,
  searchReportRequestHandler(SearchReportType.Campaign),
  getExplicitReportThunkOptions('campaignReport'),
);

export const getAdGroupReport = createAsyncThunk<IBaseDataReport<IAdGroupReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.AdGroupReport,
  searchReportRequestHandler(SearchReportType.AdGroup),
  getExplicitReportThunkOptions('adGroupReport'),
);

export const getKeywordReport = createAsyncThunk<IBaseDataReport<IKeywordReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.KeywordReport,
  searchReportRequestHandler(SearchReportType.Keyword),
  getExplicitReportThunkOptions('keywordReport'),
);
//#endregion

//#region dispatch map
type ActionSet = { action: AsyncThunk<any, any, any>; reportRequest: ReportRequest };

export const reportDispatch: Record<SearchReportView, ActionSet> = {
  [ReportView.Date]: { action: getDateReport, reportRequest: ReportRequest.Date },
  [ReportView.Platform]: { action: getPlatformReport, reportRequest: ReportRequest.Platform },
  [ReportView.Advertiser]: { action: getAdvertiserReport, reportRequest: ReportRequest.Advertiser },
  [ReportView.Campaign]: { action: getCampaignReport, reportRequest: ReportRequest.Campaign },
  [ReportView.AdGroup]: { action: getAdGroupReport, reportRequest: ReportRequest.AdGroup },
  [ReportView.Keyword]: { action: getKeywordReport, reportRequest: ReportRequest.Keyword },
};

//#endregion

//#region reducers
export const reportMap: RequestMap<IState> = [
  ['dateReport', getDateReport],
  ['platformReport', getPlatformReport],
  ['advertiserReport', getAdvertiserReport],
  ['adGroupReport', getAdGroupReport],
  ['keywordReport', getKeywordReport],
];
//#endregion
